import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback } from 'react';
import { X, Trash2, AlertTriangle } from 'lucide-react';
export const RemoveProjectConfirmModal = ({ isOpen, project, onClose, onConfirm }) => {
    const [isRemoving, setIsRemoving] = useState(false);
    // Reset removing state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setIsRemoving(false);
        }
    }, [isOpen]);
    const handleConfirm = useCallback(async () => {
        if (!project)
            return;
        setIsRemoving(true);
        try {
            await onConfirm(project.id);
            // Modal will be closed by parent component
        }
        catch (error) {
            console.error('Failed to remove project:', error);
            // In dummy implementation, we don't show error UI, just log
        }
        finally {
            setIsRemoving(false);
        }
    }, [project, onConfirm]);
    const handleCancel = useCallback(() => {
        if (!isRemoving) {
            onClose();
        }
    }, [isRemoving, onClose]);
    // Handle ESC key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen && !isRemoving) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, isRemoving, onClose]);
    if (!isOpen || !project)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 flex items-center justify-center z-50", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: () => {
                    // Allow clicking backdrop to close modal (if not removing)
                    if (!isRemoving) {
                        onClose();
                    }
                } }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative max-w-md w-full mx-4 flex flex-col", style: { maxHeight: '92vh' }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: [_jsxs("div", { className: "flex items-center space-x-3", children: [_jsx(AlertTriangle, { size: 20, style: { color: '#ef4444' } }), _jsx("h3", { className: "text-lg font-semibold", style: { color: 'var(--sand)' }, children: "Remove Project" })] }), _jsx("button", { onClick: handleCancel, disabled: isRemoving, className: "p-1 rounded hover:bg-opacity-20 hover:bg-white transition-colors", style: { color: 'var(--sand)' }, children: _jsx(X, { size: 20 }) })] }), _jsxs("div", { className: "p-6 overflow-y-auto min-h-0 flex-1", style: { backgroundColor: 'var(--sand)' }, children: [_jsxs("div", { className: "mb-4", children: [_jsx("p", { className: "text-sm mb-3", style: { color: 'var(--charcoal)' }, children: "Are you sure you want to remove the following project?" }), _jsxs("div", { className: "p-3 rounded-lg border", style: {
                                            backgroundColor: 'var(--dew)',
                                            borderColor: 'var(--pewter)',
                                            color: 'var(--charcoal)'
                                        }, children: [_jsx("div", { className: "font-medium text-sm", children: project.name }), _jsx("div", { className: "text-xs mt-1 opacity-75", children: project.description })] })] }), _jsxs("div", { className: "space-y-2 text-sm", children: [_jsxs("div", { className: "flex items-center space-x-2", style: { color: '#ef4444' }, children: [_jsx(AlertTriangle, { size: 16 }), _jsx("span", { className: "font-medium", children: "This action cannot be undone" })] }), _jsx("p", { className: "text-xs pl-6", style: { color: 'var(--charcoal)', opacity: 0.7 }, children: "All project data and associated files will be permanently deleted." })] })] }), _jsxs("div", { className: "flex justify-end space-x-3 p-6 ls-modal-band-bottom shrink-0", style: { backgroundColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: handleCancel, disabled: isRemoving, className: "px-4 py-2 text-sm rounded-lg border transition-colors", style: {
                                    borderColor: 'var(--sand)',
                                    color: 'var(--sand)',
                                    backgroundColor: 'transparent'
                                }, children: "Cancel" }), _jsx("button", { onClick: handleConfirm, disabled: isRemoving, className: "px-4 py-2 text-sm rounded-lg transition-colors flex items-center space-x-2", style: {
                                    backgroundColor: isRemoving ? '#dc2626' : '#dc2626',
                                    color: 'white',
                                    opacity: isRemoving ? 0.7 : 1
                                }, children: isRemoving ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "animate-spin rounded-full h-4 w-4 border-b-2 border-white" }), _jsx("span", { children: "Removing..." })] })) : (_jsxs(_Fragment, { children: [_jsx(Trash2, { size: 16 }), _jsx("span", { children: "Remove Project" })] })) })] })] })] }));
};
